import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Copy, Check } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { apiValidateCoupon, apiCreateOrder, apiGetSettings, resolveImg, apiCreateRazorpayOrder, apiVerifyRazorpayPayment } from "../api";

const Field = ({ label, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-neutral-700 mb-1.5">{label}</label>
    <input
      {...props}
      className="w-full border border-neutral-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-[#C8932F] transition-colors"
    />
  </div>
);

const Checkout = () => {
  const { items, cartTotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    email: user?.email || "",
    address: "",
    city: "",
    pincode: "",
  });
  const [paymentMethod, setPaymentMethod] = useState("razorpay");
  const [whatsappOptIn, setWhatsappOptIn] = useState(true);
  const [discount, setDiscount] = useState(0);
  const [placing, setPlacing] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [settings, setSettings] = useState(null);
  const [upiTxnId, setUpiTxnId] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    apiGetSettings().then(setSettings).catch(() => setSettings(null));
  }, []);

  const copyUpiId = () => {
    if (!settings?.upiId) return;
    navigator.clipboard.writeText(settings.upiId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const couponCode = localStorage.getItem("dg_coupon") || "";
  const shipping = cartTotal > 999 ? 0 : 79;
  const grandTotal = cartTotal - discount + shipping;

  useEffect(() => {
    if (couponCode && cartTotal > 0) {
      apiValidateCoupon(couponCode, cartTotal)
        .then((r) => setDiscount(r.valid ? r.discountAmount : 0))
        .catch(() => setDiscount(0));
    }
  }, [couponCode, cartTotal]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleRazorpayPayment = async () => {
    setError("");
    if (!form.name || !form.phone || !form.address) {
      setError("Please fill name, phone and address.");
      return;
    }
    setPlacing(true);
    try {
      const amountPaise = grandTotal * 100;
      const rzOrder = await apiCreateRazorpayOrder(amountPaise);

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: rzOrder.amount,
        currency: rzOrder.currency,
        name: "Deegford Fine Fragrances",
        description: `Order for ${form.name}`,
        order_id: rzOrder.order_id,
        prefill: {
          name: form.name,
          email: form.email || "",
          contact: form.phone,
        },
        theme: { color: "#C8932F" },
        handler: async (response) => {
          try {
            const orderItems = items.map((i) => ({
              id: String(i.id), name: i.name, slug: i.slug, image: i.image,
              size: i.size, price: i.price, mrp: i.mrp, qty: i.qty,
            }));
            const created = await apiVerifyRazorpayPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              items: orderItems,
              customer: form,
              couponCode: couponCode || null,
              whatsappOptIn,
            });
            setOrder(created);
            clearCart();
            localStorage.removeItem("dg_coupon");
            window.scrollTo(0, 0);
          } catch (err) {
            setError(err?.response?.data?.detail || "Payment verified but order creation failed. Contact support.");
          } finally {
            setPlacing(false);
          }
        },
        modal: {
          ondismiss: () => {
            setError("Payment cancelled. Please try again.");
            setPlacing(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        setError(`Payment failed: ${response.error.description}`);
        setPlacing(false);
      });
      rzp.open();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not initiate payment. Try again.");
      setPlacing(false);
    }
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (paymentMethod === "razorpay") {
      await handleRazorpayPayment();
      return;
    }
    setError("");
    if (!form.name || !form.phone || !form.address) {
      setError("Please fill name, phone and address.");
      return;
    }
    setPlacing(true);
    try {
      const payload = {
        items: items.map((i) => ({
          id: String(i.id), name: i.name, slug: i.slug, image: i.image,
          size: i.size, price: i.price, mrp: i.mrp, qty: i.qty,
        })),
        customer: form,
        couponCode: couponCode || null,
        paymentMethod,
        upiTransactionId: paymentMethod === "upi" ? (upiTxnId || null) : null,
        whatsappOptIn,
      };
      const created = await apiCreateOrder(payload);
      setOrder(created);
      clearCart();
      localStorage.removeItem("dg_coupon");
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not place order. Try again.");
    } finally {
      setPlacing(false);
    }
  };

  if (order) {
    const isUpi = order.paymentMethod === "upi";
    return (
      <div className="py-24 text-center px-4">
        <CheckCircle2 className="h-16 w-16 text-[#C8932F] mx-auto" />
        <h1 className="font-serif text-3xl text-neutral-900 mt-6">
          {isUpi ? "Order Received!" : "Order Placed Successfully!"}
        </h1>
        <p className="text-neutral-600 mt-3">
          Your order <span className="font-semibold text-[#C8932F]">{order.orderNumber}</span>{" "}
          {isUpi ? "has been received." : "is confirmed."}
        </p>
        <p className="text-neutral-500 mt-1 text-sm max-w-md mx-auto">
          Total ₹{order.total.toLocaleString("en-IN")} • Payment: {order.paymentMethod === "cod" ? "Cash on Delivery" : "UPI"}
          {order.whatsappOptIn ? " • WhatsApp updates enabled" : ""}
        </p>
        {isUpi && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-sm max-w-md mx-auto mt-3 py-2 px-4">
            We'll verify your UPI payment and confirm your order shortly. Track status anytime in My Orders.
          </p>
        )}
        {order.customer?.email && (
          <p className="text-sm text-neutral-400 mt-2">Order confirmation sent to {order.customer.email}</p>
        )}
        <div className="flex items-center justify-center gap-4 mt-8">
          <Link to="/profile" className="bg-gradient-to-br from-[#FFE29A] to-[#C8932F] text-[#050304] px-8 py-3 text-sm font-semibold uppercase tracking-widest rounded-sm hover:opacity-90 transition-opacity">
            View Orders
          </Link>
          <Link to="/" className="border border-neutral-300 text-neutral-600 px-8 py-3 text-sm font-semibold uppercase tracking-widest rounded-sm hover:border-neutral-400 transition-colors">
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="py-28 text-center px-4">
        <h1 className="font-serif text-2xl text-neutral-900">Your cart is empty</h1>
        <Link to="/collections/all" className="mt-6 inline-block text-[#C8932F] underline">Browse fragrances</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-4 lg:px-8 py-10">
      <h1 className="font-serif text-3xl text-neutral-900 mb-8">Checkout</h1>
      <form onSubmit={placeOrder} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-neutral-200 rounded-sm p-6">
            <h2 className="font-serif text-xl text-neutral-900 mb-5">Delivery Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full Name *" value={form.name} onChange={update("name")} placeholder="Your name" />
              <Field label="Phone *" value={form.phone} onChange={update("phone")} placeholder="+91 ..." />
              <Field label="Email" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" />
              <Field label="Pincode" value={form.pincode} onChange={update("pincode")} placeholder="560001" />
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-neutral-700 mb-1.5">Address *</label>
                <textarea value={form.address} onChange={update("address")} rows={3} placeholder="House no, street, area"
                  className="w-full border border-neutral-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-[#C8932F]" />
              </div>
              <Field label="City" value={form.city} onChange={update("city")} placeholder="City" />
            </div>

            <label className="mt-5 flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={whatsappOptIn} onChange={(e) => setWhatsappOptIn(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[#C8932F]" />
              <span className="text-sm text-neutral-700">
                Send me order updates & exclusive offers on <span className="font-semibold text-[#25D366]">WhatsApp</span>
                <span className="block text-xs text-neutral-400">You can opt out anytime.</span>
              </span>
            </label>
          </div>

          <div className="bg-white border border-neutral-200 rounded-sm p-6">
            <h2 className="font-serif text-xl text-neutral-900 mb-4">Payment Method</h2>
            {[
              { k: "razorpay", t: "Pay Online", d: "Credit/Debit card, UPI, Netbanking via Razorpay" },
              { k: "cod", t: "Cash on Delivery", d: "Pay when you receive" },
              { k: "upi", t: "UPI Payment", d: "Scan QR & pay with any UPI app" },
            ].map((p) => (
              <label key={p.k} className={`flex items-center gap-3 border rounded-sm p-4 mb-3 cursor-pointer transition-colors ${paymentMethod === p.k ? "border-[#C8932F] bg-[#faf6ef]" : "border-neutral-300"}`}>
                <input type="radio" name="pay" checked={paymentMethod === p.k} onChange={() => setPaymentMethod(p.k)} className="h-4 w-4 accent-[#C8932F]" />
                <span>
                  <span className="text-sm font-medium text-neutral-900 block">{p.t}</span>
                  <span className="text-xs text-neutral-500">{p.d}</span>
                </span>
              </label>
            ))}

            {paymentMethod === "upi" && (
              <div className="mt-2 border border-neutral-200 rounded-sm p-4 bg-[#faf6ef]">
                {settings?.upiQrImage ? (
                  <div className="flex flex-col items-center text-center">
                    <img
                      src={resolveImg(settings.upiQrImage)}
                      alt="UPI QR Code"
                      className="h-48 w-48 object-contain border border-neutral-200 rounded-sm bg-white p-2"
                    />
                    <p className="text-sm font-medium text-neutral-800 mt-3">{settings.upiPayeeName || "Deegford"}</p>
                    {settings.upiId && (
                      <button type="button" onClick={copyUpiId}
                        className="mt-1.5 flex items-center gap-1.5 text-sm text-[#C8932F] hover:underline">
                        {settings.upiId} {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      </button>
                    )}
                    <p className="text-xs text-neutral-500 mt-2">
                      Scan &amp; pay <strong>₹{grandTotal.toLocaleString("en-IN")}</strong>, then place your order.
                      We'll verify your payment and dispatch once confirmed.
                    </p>
                    <div className="w-full mt-4">
                      <label className="block text-xs font-medium text-neutral-600 mb-1 text-left">
                        UPI Transaction ID / UTR (optional, helps us verify faster)
                      </label>
                      <input
                        value={upiTxnId}
                        onChange={(e) => setUpiTxnId(e.target.value)}
                        placeholder="e.g. 123456789012"
                        className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#C8932F]"
                      />
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-neutral-500 text-center">
                    UPI QR code is not set up yet. Please choose Cash on Delivery or contact us.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white border border-neutral-200 rounded-sm p-6 sticky top-28">
            <h2 className="font-serif text-xl text-neutral-900 mb-4">Your Order</h2>
            <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
              {items.map((i) => (
                <div key={i.key} className="flex gap-3">
                  <img src={resolveImg(i.image)} alt={i.name} className="h-14 w-12 object-cover rounded-sm bg-[#f6f3ee]" />
                  <div className="flex-1 text-sm">
                    <p className="font-medium text-neutral-800 leading-tight">{i.name}</p>
                    <p className="text-xs text-neutral-500">{i.size} × {i.qty}</p>
                  </div>
                  <span className="text-sm font-semibold">₹{(i.price * i.qty).toLocaleString("en-IN")}</span>
                </div>
              ))}
            </div>
            <div className="space-y-2 text-sm border-t border-neutral-200 mt-4 pt-4">
              <div className="flex justify-between"><span className="text-neutral-600">Subtotal</span><span>₹{cartTotal.toLocaleString("en-IN")}</span></div>
              {discount > 0 && <div className="flex justify-between text-[#D6425F]"><span>Coupon ({couponCode})</span><span>-₹{discount.toLocaleString("en-IN")}</span></div>}
              <div className="flex justify-between"><span className="text-neutral-600">Shipping</span><span>{shipping === 0 ? "Free" : `₹${shipping}`}</span></div>
            </div>
            <div className="flex justify-between items-baseline border-t border-neutral-200 mt-3 pt-3">
              <span className="font-semibold">Total</span>
              <span className="text-2xl font-bold">₹{grandTotal.toLocaleString("en-IN")}</span>
            </div>
            {error && <p className="text-xs text-red-600 mt-3">{error}</p>}
            <button type="submit" disabled={placing}
              className="mt-5 w-full bg-gradient-to-br from-[#FFE29A] to-[#C8932F] text-[#050304] py-3.5 text-sm font-semibold uppercase tracking-widest rounded-sm hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60">
              {placing ? <><Loader2 className="h-4 w-4 animate-spin" /> Placing...</> : "Place Order"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Checkout;
